import type { KbIrInline, KbIrNode } from './kbApi';

/** One line of the article table of contents. */
export type KbTocItem = {
  id: string;
  text: string;
  /** Nesting relative to the highest heading the body actually uses: 0 is
   * the top level. A body with only h3 headings gives every entry depth 0 —
   * the outline is not indented into nothing. */
  depth: number;
  step?: number;
};

/** Flatten inline nodes to plain text. Links contribute their own text;
 * media and line breaks contribute nothing. */
const inlineText = (inlines: KbIrInline[]): string =>
  inlines
    .map((inline) => {
      if (inline.type === 'text') return inline.text;
      if (inline.type === 'link') return inlineText(inline.children);
      return '';
    })
    .join('')
    .trim();

type TocHeading = {
  id: string;
  text: string;
  level: 2 | 3;
  step?: number;
};

const tocHeadings = (nodes: KbIrNode[]): TocHeading[] =>
  nodes.flatMap((node) => {
    if (node.type !== 'heading') return [];
    if (node.level !== 2 && node.level !== 3) return [];
    // A heading without an id cannot be linked to, so it cannot be an entry.
    if (!node.id) return [];

    const text = inlineText(node.children);

    if (!text) return [];

    // The step number is the badge the author typed at the head of the
    // heading: `[3]` or `(3)`. The hub lifts it out of the text.
    const badge = node.children.find((child) => child.type === 'badge');

    return [
      {
        id: node.id,
        text,
        level: node.level,
        ...(badge?.type === 'badge' ? { step: badge.number } : {}),
      },
    ];
  });

/**
 * The contents list is derived, never authored: it is exactly the h2 and h3
 * headings of the body, in document order. An entry that should not be there
 * is a heading that should not be there — remove the heading.
 *
 * h1 is the page title (outside the body) and h4 is a compact step heading,
 * so neither earns a line.
 */
export const kbToc = (nodes: KbIrNode[]): KbTocItem[] => {
  const headings = tocHeadings(nodes);

  if (headings.length === 0) return [];

  const topLevel = Math.min(...headings.map((heading) => heading.level));

  return headings.map(({ id, text, level, step }) => ({
    id,
    text,
    depth: level - topLevel,
    ...(step !== undefined ? { step } : {}),
  }));
};
